/**
 * Semantic Design Tokens
 *
 * Purpose-driven abstraction layer over raw Figma tokens.
 * See README.md for usage examples.
 */

import {
  STYLES_BLUE_BLUE_10,
  STYLES_BLUE_BLUE_20,
  STYLES_BLUE_BLUE_30,
  STYLES_BLUE_BLUE_50,
  STYLES_BLUE_BLUE_60,
  STYLES_BLUE_BLUE_70,
  STYLES_BLUE_BLUE_80,
  STYLES_BRAND_COLORS_BLACK,
  STYLES_BRAND_COLORS_SYNGENTA_GREEN,
  STYLES_BRAND_COLORS_SYNGENTA_NAVY,
  STYLES_BRAND_COLORS_WHITE,
  STYLES_GREEN_GREEN_10,
  STYLES_GREEN_GREEN_20,
  STYLES_GREEN_GREEN_30,
  STYLES_GREEN_GREEN_50,
  STYLES_GREEN_GREEN_60,
  STYLES_GREEN_GREEN_70,
  STYLES_GREEN_GREEN_80,
  // Color tokens
  STYLES_NEUTRAL_NEUTRAL_00,
  STYLES_NEUTRAL_NEUTRAL_10,
  STYLES_NEUTRAL_NEUTRAL_100,
  STYLES_NEUTRAL_NEUTRAL_20,
  STYLES_NEUTRAL_NEUTRAL_30,
  STYLES_NEUTRAL_NEUTRAL_40,
  STYLES_NEUTRAL_NEUTRAL_50,
  STYLES_NEUTRAL_NEUTRAL_60,
  STYLES_NEUTRAL_NEUTRAL_70,
  STYLES_NEUTRAL_NEUTRAL_80,
  STYLES_NEUTRAL_NEUTRAL_90,
  STYLES_ORANGE_ORANGE_10,
  STYLES_ORANGE_ORANGE_20,
  STYLES_ORANGE_ORANGE_30,
  STYLES_ORANGE_ORANGE_50,
  STYLES_ORANGE_ORANGE_60,
  STYLES_ORANGE_ORANGE_70,
  STYLES_ORANGE_ORANGE_80,
  STYLES_PURPLE_PURPLE_10,
  STYLES_PURPLE_PURPLE_20,
  STYLES_PURPLE_PURPLE_30,
  STYLES_PURPLE_PURPLE_50,
  STYLES_PURPLE_PURPLE_60,
  STYLES_PURPLE_PURPLE_70,
  STYLES_PURPLE_PURPLE_80,
  STYLES_RED_RED_10,
  STYLES_RED_RED_20,
  STYLES_RED_RED_30,
  STYLES_RED_RED_50,
  STYLES_RED_RED_60,
  STYLES_RED_RED_70,
  STYLES_RED_RED_80,
  STYLES_REGULAR_10PX_14PX,
  STYLES_REGULAR_12PX_16PX,
  STYLES_REGULAR_14PX_20PX,
  STYLES_REGULAR_16PX_24PX,
  STYLES_REGULAR_20PX_28PX,
  STYLES_REGULAR_24PX_32PX,
  STYLES_REGULAR_32PX_40PX,
  STYLES_REGULAR_40PX_52PX,
  STYLES_REGULAR_56PX_64PX,
  // Typography tokens
  STYLES_REGULAR_8PX_12PX,
  STYLES_SEMIBOLD_10PX_14PX,
  STYLES_SEMIBOLD_12PX_16PX,
  STYLES_SEMIBOLD_14PX_20PX,
  STYLES_SEMIBOLD_16PX_24PX,
  STYLES_SEMIBOLD_20PX_28PX,
  STYLES_SEMIBOLD_24PX_32PX,
  STYLES_SEMIBOLD_32PX_40PX,
  STYLES_SEMIBOLD_40PX_52PX,
  STYLES_SEMIBOLD_56PX_64PX,
  STYLES_SEMIBOLD_8PX_12PX,
  STYLES_YELLOW_YELLOW_10,
  STYLES_YELLOW_YELLOW_20,
  STYLES_YELLOW_YELLOW_30,
  STYLES_YELLOW_YELLOW_50,
  STYLES_YELLOW_YELLOW_60,
  STYLES_YELLOW_YELLOW_70,
  STYLES_YELLOW_YELLOW_80
} from './tokens';

// ============================================================================
// TYPOGRAPHY
// ============================================================================

/**
 * Typography tokens organized by semantic purpose.
 * Each level includes both regular and semibold variants.
 */
export const typography = {
  /** Hero text for landing pages and major sections (40px-56px) */
  display: {
    xl: {
      regular: STYLES_REGULAR_56PX_64PX,
      semibold: STYLES_SEMIBOLD_56PX_64PX
    },
    lg: {
      regular: STYLES_REGULAR_40PX_52PX,
      semibold: STYLES_SEMIBOLD_40PX_52PX
    },
    md: {
      regular: STYLES_REGULAR_32PX_40PX,
      semibold: STYLES_SEMIBOLD_32PX_40PX
    }
  },

  /** Headings for page titles and section headers (16px-32px) */
  heading: {
    xl: {
      regular: STYLES_REGULAR_32PX_40PX,
      semibold: STYLES_SEMIBOLD_32PX_40PX
    },
    lg: {
      regular: STYLES_REGULAR_24PX_32PX,
      semibold: STYLES_SEMIBOLD_24PX_32PX
    },
    md: {
      regular: STYLES_REGULAR_20PX_28PX,
      semibold: STYLES_SEMIBOLD_20PX_28PX
    },
    sm: {
      regular: STYLES_REGULAR_16PX_24PX,
      semibold: STYLES_SEMIBOLD_16PX_24PX
    }
  },

  /** Body text for paragraphs and content (12px-16px) */
  body: {
    lg: {
      regular: STYLES_REGULAR_16PX_24PX,
      semibold: STYLES_SEMIBOLD_16PX_24PX
    },
    md: {
      regular: STYLES_REGULAR_14PX_20PX,
      semibold: STYLES_SEMIBOLD_14PX_20PX
    },
    sm: {
      regular: STYLES_REGULAR_12PX_16PX,
      semibold: STYLES_SEMIBOLD_12PX_16PX
    }
  },

  /** Labels for form fields, buttons, and UI elements (10px-14px) */
  label: {
    lg: {
      regular: STYLES_REGULAR_14PX_20PX,
      semibold: STYLES_SEMIBOLD_14PX_20PX
    },
    md: {
      regular: STYLES_REGULAR_12PX_16PX,
      semibold: STYLES_SEMIBOLD_12PX_16PX
    },
    sm: {
      regular: STYLES_REGULAR_10PX_14PX,
      semibold: STYLES_SEMIBOLD_10PX_14PX
    }
  },

  /** Small text for captions, footnotes, and metadata (8px-10px) */
  caption: {
    md: {
      regular: STYLES_REGULAR_10PX_14PX,
      semibold: STYLES_SEMIBOLD_10PX_14PX
    },
    sm: {
      regular: STYLES_REGULAR_8PX_12PX,
      semibold: STYLES_SEMIBOLD_8PX_12PX
    }
  }
} as const;

// ============================================================================
// COLORS
// ============================================================================

/**
 * Color tokens organized by semantic purpose.
 * Use these for consistent theming across the application.
 */
export const colors = {
  /** Primary brand colors for key actions and brand identity */
  primary: {
    lightest: STYLES_GREEN_GREEN_10,
    lighter: STYLES_GREEN_GREEN_20,
    light: STYLES_GREEN_GREEN_30,
    base: STYLES_BRAND_COLORS_SYNGENTA_GREEN, // Core brand green
    dark: STYLES_GREEN_GREEN_60,
    darker: STYLES_GREEN_GREEN_70,
    darkest: STYLES_GREEN_GREEN_80
  },

  /** Secondary brand colors for supporting elements */
  secondary: {
    lightest: STYLES_BLUE_BLUE_10,
    lighter: STYLES_BLUE_BLUE_20,
    light: STYLES_BLUE_BLUE_30,
    base: STYLES_BLUE_BLUE_50,
    dark: STYLES_BLUE_BLUE_70,
    darker: STYLES_BLUE_BLUE_80,
    darkest: STYLES_BRAND_COLORS_SYNGENTA_NAVY
  },

  /** Success states and positive feedback */
  success: {
    lightest: STYLES_GREEN_GREEN_10,
    lighter: STYLES_GREEN_GREEN_20,
    light: STYLES_GREEN_GREEN_30,
    base: STYLES_GREEN_GREEN_50,
    dark: STYLES_GREEN_GREEN_60,
    darker: STYLES_GREEN_GREEN_70,
    darkest: STYLES_GREEN_GREEN_80
  },

  /** Error states and destructive actions */
  error: {
    lightest: STYLES_RED_RED_10,
    lighter: STYLES_RED_RED_20,
    light: STYLES_RED_RED_30,
    base: STYLES_RED_RED_50,
    dark: STYLES_RED_RED_60,
    darker: STYLES_RED_RED_70,
    darkest: STYLES_RED_RED_80
  },

  /** Warning states and cautionary messages */
  warning: {
    lightest: STYLES_YELLOW_YELLOW_10,
    lighter: STYLES_YELLOW_YELLOW_20,
    light: STYLES_YELLOW_YELLOW_30,
    base: STYLES_YELLOW_YELLOW_50,
    dark: STYLES_YELLOW_YELLOW_60,
    darker: STYLES_YELLOW_YELLOW_70,
    darkest: STYLES_YELLOW_YELLOW_80
  },

  /** Informational states and neutral feedback */
  info: {
    lightest: STYLES_BLUE_BLUE_10,
    lighter: STYLES_BLUE_BLUE_20,
    light: STYLES_BLUE_BLUE_30,
    base: STYLES_BLUE_BLUE_50,
    dark: STYLES_BLUE_BLUE_60,
    darker: STYLES_BLUE_BLUE_70,
    darkest: STYLES_BLUE_BLUE_80
  },

  /** Accent colors for special highlights and decorative elements */
  accent: {
    purple: {
      lightest: STYLES_PURPLE_PURPLE_10,
      lighter: STYLES_PURPLE_PURPLE_20,
      light: STYLES_PURPLE_PURPLE_30,
      base: STYLES_PURPLE_PURPLE_50,
      dark: STYLES_PURPLE_PURPLE_60,
      darker: STYLES_PURPLE_PURPLE_70,
      darkest: STYLES_PURPLE_PURPLE_80
    },
    orange: {
      lightest: STYLES_ORANGE_ORANGE_10,
      lighter: STYLES_ORANGE_ORANGE_20,
      light: STYLES_ORANGE_ORANGE_30,
      base: STYLES_ORANGE_ORANGE_50,
      dark: STYLES_ORANGE_ORANGE_60,
      darker: STYLES_ORANGE_ORANGE_70,
      darkest: STYLES_ORANGE_ORANGE_80
    }
  },

  /** Neutral grayscale for text, borders, and backgrounds */
  neutral: {
    white: STYLES_NEUTRAL_NEUTRAL_00,
    lightest: STYLES_NEUTRAL_NEUTRAL_10,
    lighter: STYLES_NEUTRAL_NEUTRAL_20,
    light: STYLES_NEUTRAL_NEUTRAL_30,
    base: STYLES_NEUTRAL_NEUTRAL_50,
    dark: STYLES_NEUTRAL_NEUTRAL_60,
    darker: STYLES_NEUTRAL_NEUTRAL_70,
    darkest: STYLES_NEUTRAL_NEUTRAL_80,
    black: STYLES_NEUTRAL_NEUTRAL_100
  },

  /** Text colors for different content hierarchy levels */
  text: {
    primary: STYLES_NEUTRAL_NEUTRAL_100, // Main content
    secondary: STYLES_NEUTRAL_NEUTRAL_70, // Supporting content
    tertiary: STYLES_NEUTRAL_NEUTRAL_50, // Subtle content
    disabled: STYLES_NEUTRAL_NEUTRAL_40, // Disabled state
    onPrimary: STYLES_BRAND_COLORS_WHITE, // Text on primary color backgrounds
    onDark: STYLES_BRAND_COLORS_WHITE // Text on dark backgrounds
  },

  /** Background colors for surfaces and containers */
  background: {
    default: STYLES_BRAND_COLORS_WHITE, // Main page background
    subtle: STYLES_NEUTRAL_NEUTRAL_10, // Subtle background
    surface: STYLES_NEUTRAL_NEUTRAL_00, // Cards, panels
    elevated: STYLES_BRAND_COLORS_WHITE, // Modals, popovers
    overlay: STYLES_NEUTRAL_NEUTRAL_90, // Modal backdrop
    disabled: STYLES_NEUTRAL_NEUTRAL_20 // Disabled components
  },

  /** Border colors for dividers and component outlines */
  border: {
    default: STYLES_NEUTRAL_NEUTRAL_30, // Standard borders
    subtle: STYLES_NEUTRAL_NEUTRAL_20, // Subtle dividers
    strong: STYLES_NEUTRAL_NEUTRAL_50, // Emphasized borders
    focus: STYLES_BLUE_BLUE_50, // Focus indicators
    error: STYLES_RED_RED_50, // Error borders
    success: STYLES_GREEN_GREEN_50 // Success borders
  },

  /** Brand colors for consistency with brand guidelines */
  brand: {
    green: STYLES_BRAND_COLORS_SYNGENTA_GREEN,
    navy: STYLES_BRAND_COLORS_SYNGENTA_NAVY,
    white: STYLES_BRAND_COLORS_WHITE,
    black: STYLES_BRAND_COLORS_BLACK
  }
} as const;
